import React, { useState } from 'react';

const Search = () => {
  const [selectedDataset, setSelectedDataset] = useState('');
  const [geneName, setGeneName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [dataset1Images, setDataset1Images] = useState(null);
  
  // Sample datasets
  const datasets = [
    { id: 'dataset1', name: 'MM IFM/DFCI 2009 RNAseq' },
    { id: 'dataset2', name: 'MM IFM/DFCI 2009 WGS' },
    { id: 'dataset3', name: 'MM GAMER High Risk' },
    { id: 'dataset4', name: 'MM GAMER t(4;14) & del17p' },
    { id: 'dataset5', name: 'SMM & MGUS Precursor 1' },
    { id: 'dataset6', name: 'MM Determination' },
  ];
  
  // Sample dataset info
  const datasetInfo = {
    dataset1: {
      title: 'MM IFM/DFCI 2009 RNAseq',
      description: 'Comprehensive transcriptome analysis of multiple myeloma samples collected  from IFM 2009 Phase III study. Patients were treated with either RVD or RVD+ASCT at the first line.',
      sampleSize: '319 RNAseq and 183 Whole Genome Sequencing. RNAseq dataset also include 16 normal plasma cell samples.',
      platform: 'Unstranded RNA sequencing. All samples were QCed post sequencing and only samples with at least 20M reads were kept. Samples were quantified using Salmon and Gencode v24 annotations. Raw read counts were processed using DESeq2 for normalization and differential gene expression analysis. Differential gene expression between newly diagnosed and MM samples are calculated and FDR corrected using DEseq2. Comparison between cytogenetics groups are calculated using t test without correction. KM plots are generated using R. All figures are generated in R. ',
      publicationDate: 'Original dataset publication was 2017. Since the first full publication dataset has been updated at various times.',
      contact: 'FISH based methods used to define subgroups. Only t(11;14) cohort is defined from RNA-seq using CCND1 and CCND2 expression ratio.'
    },
    dataset2: {
      title: 'MM IFM/DFCI 2009 WGS',
      description: 'Whole genome sequencing data from newly diagnosed multiple myeloma patients enrolled in IFM2009 study.',
      sampleSize: 183,
      platform: 'Deep whole genome sequencing performed on tumor and germline samples from newly diagnosed MM patients.',
      publicationDate: 'Coming soon...',
      contact: '...'
    },
    dataset3: {
      title: 'MM GAMER High Risk',
      description: 'Whole genome sequencing data from newly diagnosed multiple myeloma patients.',
      sampleSize: 0,
      platform: 'Deep whole genome sequencing performed on tumor and germline samples from newly diagnosed MM patients.',
      publicationDate: 'Coming soon...',
      contact: '...'
    },
    dataset4: {
      title: 'MM GAMER t(4;14) & del17p',
      description: 'Whole genome sequencing data from newly diagnosed multiple myeloma patients.',
      sampleSize: 0,
      platform: 'Deep whole genome sequencing performed on tumor and germline samples from newly diagnosed MM patients.',
      publicationDate: 'Coming soon...',
      contact: '...'
    },
    dataset5: {
      title: 'SMM & MGUS Precursor 1',
      description: 'Whole genome sequencing data from newly diagnosed multiple myeloma patients.',
      sampleSize: 0,
      platform: 'Deep whole genome sequencing performed on tumor and germline samples from newly diagnosed MM patients.',
      publicationDate: 'Coming soon...',
      contact: '...'
    },
    dataset6: {
      title: 'MM Determination',
      description: 'Whole genome sequencing data from newly diagnosed multiple myeloma patients.',
      sampleSize: 0,
      platform: 'Deep whole genome sequencing performed on tumor and germline samples from newly diagnosed MM patients.',
      publicationDate: 'Coming soon...',
      contact: '...'
    },
    // More datasets info...
  };
  
// Loading Spinner Component
  const LoadingSpinner = () => (
    <div className="loading-overlay">
      <div className="loading-content">
        <div className="spinner"></div>
        <p>Analysis is running...</p>
      </div>
    </div>
  );

  // Response Modal Component
  const ResponseModal = ({ data, onClose }) => (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Analysis Results</h3>
          <button onClick={onClose} className="close-button">&times;</button>
        </div>
        <div className="modal-body">
          <div className="response-images">
            {data.image_a_url && (
              <div className="image-container">
                <h4>Histogram A</h4>
                <img src={data.image_a_url} alt="Histogram A" className="result-image" />
              </div>
            )}
            {data.image_b_url && (
              <div className="image-container">
                <h4>Histogram B</h4>
                <img src={data.image_b_url} alt="Histogram B" className="result-image" />
              </div>
            )}
          </div>
          <div className="raw-json">
            <h4>Raw Response:</h4>
            <pre>{JSON.stringify(data, null, 2)}</pre>
          </div>
        </div>
      </div>
    </div>
  );
  
  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!selectedDataset || !geneName.trim()) {
      alert('Please select a dataset and enter a gene name');
      return;
    }

    setIsLoading(true);
    setDataset1Images(null); // Clear previous images
    
    try {
      if (selectedDataset === 'dataset1') {
        // Call the IFM API for dataset1
        const response = await fetch(`https://rconnect.dfci.harvard.edu/content/1ff3bf29-d422-4958-aef3-ecfd2f9639f6/checkIFM?geneName=${encodeURIComponent(geneName.trim())}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setDataset1Images(data);
      } else {
        // Call the regular API for other datasets
        const response = await fetch('https://rconnect.dfci.harvard.edu/content/1ff3bf29-d422-4958-aef3-ecfd2f9639f6/checkGene', {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setApiResponse(data);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('An error occurred while fetching the data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to extract filename from URL
  const getFilenameFromUrl = (url) => {
    if (!url) return "image";
    const parts = url.split('/');
    return parts[parts.length - 1] || "image";
  };
  
  return (
    <div className="search-dashboard">
      <h1>Search Database</h1>
      
      {/* Status Boxes */}
      <div className="status-boxes">
        <div className="status-box good">
          <h3>Thank you!</h3>
          <p>Depending on the current server load, it may take a minute to return your results. We appreciate your understanding and cooperation as we work to provide the best experience possible. Please cite our portal and datasets when you use the data and images.</p>
        </div>
        <div className="status-box warning">
          <h3>Report Bugs</h3>
          <p>Please report any problems you encounter. There are some known bugs we are still working to fix. If your gene is not returning any results, try using alternative gene symbols.</p>
        </div>
        <div className="status-box danger">
          <h3>Warning</h3>
          <p>The data and information provided on this portal are intended for research purposes only and should not be used for making any clinical decisions, diagnoses, or providing clinical care. The content on this platform is not a substitute for professional medical advice, diagnosis, or treatment.</p>
        </div>
      </div>
      
      <div className="dashboard-content">
        {/* Left Panel - Search Controls */}
        <div className="left-panel">
          <form onSubmit={handleSearch}>
            <div className="form-group">
              <label htmlFor="dataset-select">Select Dataset</label>
              <select 
                id="dataset-select"
                value={selectedDataset}
                onChange={(e) => setSelectedDataset(e.target.value)}
                className="dataset-dropdown"
              >
                <option value="">-- Select a dataset --</option>
                {datasets.map(dataset => (
                  <option key={dataset.id} value={dataset.id}>
                    {dataset.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="gene-input">Gene Name</label>
              <input
                type="text"
                id="gene-input"
                placeholder="e.g., KRAS"
                value={geneName}
                onChange={(e) => setGeneName(e.target.value.toUpperCase())}
                className="gene-input"
                style={{ textTransform: 'uppercase' }}
              />
              <p className="input-help">Enter a single gene name (e.g., KRAS, TP53)</p>
            </div>
            
            <button type="submit" className="search-button">
              Search
            </button>
          </form>
        </div>
        
        {/* Main Content Area */}
<div className="main-content-area">
  {/* Changed from content-row to content-stack */}
  <div className="content-stack">
    {/* Dataset Information - Now full width on top */}
    <div className="dataset-info">
      <h2>Dataset Information</h2>
      {selectedDataset ? (
        <div className="dataset-details">
          <h3>{datasetInfo[selectedDataset]?.title}</h3>
          <p className="description">{datasetInfo[selectedDataset]?.description}</p>
          <table className="info-table">
            <tbody>
              <tr>
                <td>Sample Size:</td>
                <td>{datasetInfo[selectedDataset]?.sampleSize}</td>
              </tr>
              <tr>
                <td>Platform:</td>
                <td>{datasetInfo[selectedDataset]?.platform}</td>
              </tr>
              <tr>
                <td>Publication Date:</td>
                <td>{datasetInfo[selectedDataset]?.publicationDate}</td>
              </tr>
              <tr>
                <td>What type of data used for subgroups:</td>
                <td>{datasetInfo[selectedDataset]?.contact}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <p className="select-prompt">Please select a dataset to view information</p>
      )}
    </div>
    
    {/* Results Section - Now full width below */}
    <div className="results-section">
      <h2>Results</h2>
      
      {dataset1Images ? (
        <div className="dataset1-images">
          {/* Image content - remains the same */}
          {Object.entries(dataset1Images).map(([key, url]) => {
            if ((key.toLowerCase().includes('url') || key.toLowerCase().includes('image')) && url) {
              return (
                <div key={key} className="dataset1-image-container">
                  <h4>{key.replace(/_/g, ' ').replace(/url/i, '').trim() || 'Image'}</h4>
                  <div className="image-with-download">
                    <img src={url} alt={key} className="dataset1-image" />
                    <a 
                      href={url} 
                      download={getFilenameFromUrl(url)}
                      className="download-icon-button"
                      title="Download image"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                    </a>
                  </div>
                </div>
              );
            }
            return null;
          })}
        </div>
      ) : selectedDataset ? (
        <div className="stacked-images">
          {/* Placeholder content - remains the same */}
          <div className="image-container">
            <h4>Gene Expression Heatmap</h4>
            <div className="placeholder-image">
              <div className="image-placeholder" style={{height: "200px", backgroundColor: "#f0f0f0"}}>
                Gene Expression Heatmap Image
              </div>
            </div>
          </div>
          
          <div className="image-container">
            <h4>Principal Component Analysis</h4>
            <div className="placeholder-image">
              <div className="image-placeholder" style={{height: "200px", backgroundColor: "#f0f0f0"}}>
                PCA Plot Image
              </div>
            </div>
          </div>
          
          <div className="image-container">
            <h4>Survival Analysis</h4>
            <div className="placeholder-image">
              <div className="image-placeholder" style={{height: "200px", backgroundColor: "#f0f0f0"}}>
                Kaplan-Meier Plot Image
              </div>
            </div>
          </div>
        </div>
      ) : (
        <p className="select-prompt">Please select a dataset and search to view results</p>
      )}
    </div>
  </div>
</div>
      </div>
      
      {isLoading && <LoadingSpinner />}
      {showModal && <ResponseModal data={apiResponse} onClose={() => setShowModal(false)} />}
    </div>
  );
};

export default Search;